import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { ArrowLeft, Ship, UserPlus } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router'

import type { Role } from '@ankor-io/common/types/account'
import { AccountPreferenceKeys, OnboardingStatus } from '@ankor-io/common/types/account'

import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { Link } from '@/ui-components/Link'
import InviteUsersModal from '@/views/components/InviteUserModal'
import NewConnectedAccountModal from '@/views/components/NewConnectedAccountModal/NewConnectedAccountModal'

type ConnectedAccountSetupProps = {
  goBack: () => void
}

export const ConnectedAccountSetup = ({ goBack }: ConnectedAccountSetupProps) => {
  const { getAccessToken } = useKindeAuth()
  const navigate = useNavigate()
  const { activeOrganizationId, updatePreferences } = useUserContext()

  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false)
  const [isInviteUsersModalOpen, setIsInviteUsersModalOpen] = useState(false)

  const handleUserInvite = async (userData: { email: string; role: Role }) => {
    const inviteUser = async () => {
      try {
        const res = await fetch(`/api/pay/org/${activeOrganizationId}/users/invite`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getAccessToken()}`,
          },
          body: JSON.stringify({
            email: userData.email,
            roles: [userData.role],
          }),
        })

        if (!res.ok) {
          throw new Error('Failed to invite user')
        }

        setIsInviteUsersModalOpen(false)
      } catch (error) {
        console.error('Error inviting user:', error)
        throw new Error('Error inviting user')
      }
    }

    toast.promise(inviteUser, {
      loading: 'Inviting user...',
      success: 'User invited successfully',
      error: 'Failed to invite user',
    })
  }

  return (
    <>
      <div className="h-full flex flex-col justify-between gap-y-6 sm:gap-y-8">
        <Button
          plain
          aria-label="Go back"
          className="self-start"
          onClick={goBack}
        >
          <ArrowLeft data-slot="icon" />
          Back
        </Button>

        <div className="space-y-3 sm:space-y-4">
          <h1 className="text-2xl sm:text-2xl font-bold text-gray-900 dark:text-gray-50">Let&apos;s Get Started</h1>
          <p className="text-base sm:text-lg text-gray-500 dark:text-gray-400">
            Setup your organization name, add your vessels and invite your team members.
          </p>

          <div className="space-y-4 sm:space-y-6">
            <div className="space-y-3 sm:space-y-4">
              <Button
                color="primary"
                className="w-full"
                onClick={() => setIsAccountModalOpen(true)}
              >
                <Ship data-slot="icon" />
                Create Your First Vessel
              </Button>

              <Button
                outline
                onClick={() => setIsInviteUsersModalOpen(true)}
                className="w-full"
              >
                <UserPlus data-slot="icon" />
                Invite Team Members
              </Button>

              <Link
                to="/"
                onClick={() => {
                  updatePreferences({
                    [AccountPreferenceKeys.ONBOARDING_STATUS]: OnboardingStatus.SKIPPED,
                  })
                }}
                className="flex justify-center cursor-pointer text-xs sm:text-sm hover:underline text-gray-500 dark:text-gray-400"
              >
                Skip for now
              </Link>
            </div>

            <p className="text-xs sm:text-sm text-center text-gray-500 dark:text-gray-400">
              You'll be able to add wallets, manage expenses, and issue cards once your vessel is set up.
            </p>
          </div>
        </div>

        {/* Bottom spacer */}
        <div className="block" />
      </div>

      <NewConnectedAccountModal
        isOpen={isAccountModalOpen}
        setIsOpen={setIsAccountModalOpen}
        emitConnectedAccountCreated={() => {
          updatePreferences({
            [AccountPreferenceKeys.ONBOARDING_STATUS]: OnboardingStatus.COMPLETED,
          })
          navigate('/kyc-rfi')
        }}
      />

      <InviteUsersModal
        isOpen={isInviteUsersModalOpen}
        onClose={() => setIsInviteUsersModalOpen(false)}
        onInvite={handleUserInvite}
      />
    </>
  )
}

export default ConnectedAccountSetup
